import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Package, CheckCircle, Truck, Clock, User, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { shipmentAPI } from '../services/api';
import { statusColors } from '../data/constants';

const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState(searchParams.get('number') || '');
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTrack = async () => {
    if (!trackingNumber) {
      toast({
        title: "Enter Tracking Number",
        description: "Please enter a tracking number to track your shipment.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const data = await shipmentAPI.trackShipment(trackingNumber.toUpperCase());
      setShipment(data);
    } catch (error) {
      toast({
        title: "Not Found",
        description: "Tracking number not found. Please check and try again.",
        variant: "destructive"
      });
      setShipment(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-track if tracking number in URL
  React.useEffect(() => {
    if (searchParams.get('number')) {
      handleTrack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusSteps = [
    { label: "Order Created", icon: Package },
    { label: "Cargo Picked Up", icon: CheckCircle },
    { label: "In Transit", icon: Truck },
    { label: "Arrived at Destination Hub", icon: MapPin },
    { label: "Out for Delivery", icon: Truck },
    { label: "Delivered", icon: CheckCircle }
  ];

  const getCurrentStepIndex = () => {
    if (!shipment) return -1;
    return statusSteps.findIndex(step => step.label === shipment.status);
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#D62828] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">Track Your Shipment</h1>
          <p className="text-gray-100">Enter your tracking number to see real-time updates</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex gap-4">
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Enter tracking number (e.g., JIT-12345678)"
              className="flex-1 h-12 text-base"
              onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
            />
            <Button
              onClick={handleTrack}
              disabled={loading}
              className="bg-[#D62828] hover:bg-[#B01F1F] text-white px-8 h-12"
            >
              <Search className="w-5 h-5 mr-2" />
              {loading ? 'Searching...' : 'Track'}
            </Button>
          </div>
        </div>

        {/* Shipment Details */}
        {shipment && (
          <div className="space-y-6">
            {/* Current Status */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Tracking: {shipment.tracking_number}</h2>
                  <p className="text-gray-600">Current Status</p>
                </div>
                <span className={`px-4 py-2 rounded-full font-semibold ${statusColors[shipment.status]}`}>
                  {shipment.status}
                </span>
              </div>

              {/* Shipment Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Customer</p>
                    <p className="font-semibold">{shipment.sender_name}</p>
                    <p className="text-sm text-gray-700">{shipment.sender_email}</p>
                    <p className="text-sm text-gray-700">{shipment.sender_phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Route</p>
                    <p className="font-semibold text-sm">From: {shipment.sender_address}</p>
                    <p className="font-semibold text-sm mt-1">To: {shipment.receiver_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Service Type</p>
                    <p className="font-semibold">{shipment.service_type}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Travel Mode</p>
                    <p className="font-semibold">{shipment.travel_mode}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Size (L x W x H)</p>
                    <p className="font-semibold">{shipment.length} x {shipment.width} x {shipment.height} cm</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Weight</p>
                    <p className="font-semibold">{shipment.weight} kg</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Estimated Delivery</p>
                    <p className="font-semibold">{shipment.estimated_delivery}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#D62828] mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Delivery Type</p>
                    <p className="font-semibold">{shipment.delivery_type}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Shipment Timeline</h3>
              <div className="relative">
                {statusSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;

                  return (
                    <div key={index} className="flex items-start gap-4 mb-8 last:mb-0 relative">
                      {/* Connector Line */}
                      {index < statusSteps.length - 1 && (
                        <div
                          className={`absolute left-6 top-12 w-0.5 h-full ${
                            index < currentStepIndex ? 'bg-[#D62828]' : 'bg-gray-300'
                          }`}
                        />
                      )}

                      {/* Icon */}
                      <div
                        className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-[#D62828] text-white' : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 pt-2">
                        <p className={`font-semibold ${
                          isCurrent ? 'text-[#D62828]' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {step.label}
                        </p>
                        {isCurrent && (
                          <p className="text-sm text-gray-600 mt-1">Current status</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!shipment && !loading && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Shipment Found</h3>
            <p className="text-gray-600">Enter a tracking number to view shipment details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingPage;
